import React, { useRef, useEffect, useState } from 'react';
import { FaceMesh } from '@mediapipe/face_mesh';
import { Camera } from '@mediapipe/camera_utils';

/**
 * LivenessFaceCapture - Componente de captura facial con liveness detection
 *
 * Implementa captura multi-ángulo como apps bancarias:
 * - Centro, Izquierda, Derecha, Arriba, Abajo
 * - Validación de movimiento real (anti-spoofing)
 * - Alertas sonoras para detección de rostro
 */
const LivenessFaceCapture = ({ onComplete, onError }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);
  const progressTimerRef = useRef(null);
  const progressCountRef = useRef(0);
  const isCapturingRef = useRef(false);
  const capturedAnglesRef = useRef(new Set());
  const stepRef = useRef(1);
  const speechSynthRef = useRef(null);
  const lastFaceDetectedRef = useRef(false);
  const timeoutIdsRef = useRef([]);
  const isMountedRef = useRef(true);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      timeoutIdsRef.current.forEach(id => clearTimeout(id));
      timeoutIdsRef.current = [];
    };
  }, []);

  const addTimeout = (callback, delay) => {
    const id = setTimeout(() => {
      timeoutIdsRef.current = timeoutIdsRef.current.filter(t => t !== id);
      if (isMountedRef.current) callback();
    }, delay);
    timeoutIdsRef.current.push(id);
    return id;
  };

  // Inicializar speech synthesis una sola vez
  useEffect(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      speechSynthRef.current = window.speechSynthesis;
    }
    return () => {
      if (speechSynthRef.current) {
        speechSynthRef.current.cancel();
      }
    };
  }, []);

  // Función para reproducir instrucción de voz
  const speakInstruction = (text) => {
    if (!speechSynthRef.current) return;
    
    speechSynthRef.current.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'es-ES';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    
    const voices = speechSynthRef.current.getVoices();
    const spanishVoice = voices.find(v => v.lang.includes('es'));
    if (spanishVoice) {
      utterance.voice = spanishVoice;
    }
    
    speechSynthRef.current.speak(utterance);
  };

  // Mapear instrucciones de texto a voz
  const instructionToSpeech = (instructionText) => {
    const map = {
      '👤 Mira al frente': 'Mira al frente',
      '👈 Gira a tu izquierda': 'Gira a tu izquierda',
      '👉 Gira a tu derecha': 'Gira a tu derecha',
      '👆 Mira arriba': 'Mira arriba',
      '👇 Mira abajo': 'Mira abajo'
    };
    return map[instructionText] || instructionText;
  };

  const [step, setStep] = useState(1); // 1-5: capturas
  const [capturedFrames, setCapturedFrames] = useState([]);
  const [currentInstruction, setCurrentInstruction] = useState('');
  const [headPose, setHeadPose] = useState({ yaw: 0, pitch: 0, roll: 0 });
  const [faceDetected, setFaceDetected] = useState(false);
  const [poseProgress, setPoseProgress] = useState(0); // Progreso del círculo 0-100

  // Definir pasos de captura con umbrales EXTREMADAMENTE permisivos
  // IMPORTANTE: Los valores observados muestran que cuando giras a TU izquierda, yaw es NEGATIVO
  // VERSIÓN: 2.0 - Umbrales corregidos basado en valores reales
  const captureSteps = [
    {
      id: 'center',
      instruction: '👤 Mira al frente',
      checkPose: (pose) => Math.abs(pose.yaw) < 25 && Math.abs(pose.pitch) < 25,
      color: '#3B82F6'
    },
    {
      id: 'left',
      instruction: '👈 Gira a tu izquierda',
      checkPose: (pose) => {
        const match = pose.yaw < -3;
        console.log(`🔍 [LEFT CHECK] yaw=${pose.yaw.toFixed(1)}° | umbral: < -3 | match=${match}`);
        return match;
      },
      color: '#8B5CF6'
    },
    {
      id: 'right',
      instruction: '👉 Gira a tu derecha',
      checkPose: (pose) => {
        const match = pose.yaw > 3;
        console.log(`🔍 [RIGHT CHECK] yaw=${pose.yaw.toFixed(1)}° | umbral: > 3 | match=${match}`);
        return match;
      },
      color: '#EC4899'
    },
    {
      id: 'up',
      instruction: '👆 Mira arriba',
      checkPose: (pose) => {
        const match = pose.pitch < -1.5; // Negativo cuando mira arriba (más permisivo)
        console.log(`🔍 [UP CHECK] pitch=${pose.pitch.toFixed(1)}° | umbral: < -1.5 | match=${match}`);
        return match;
      },
      color: '#10B981'
    },
    {
      id: 'down',
      instruction: '👇 Mira abajo',
      checkPose: (pose) => {
        const match = pose.pitch > 1.5; // Valores positivos cuando mira abajo (más permisivo)
        console.log(`🔍 [DOWN CHECK] pitch=${pose.pitch.toFixed(1)}° | umbral: > 1.5 | match=${match}`);
        return match;
      },
      color: '#F59E0B'
    }
  ];

  // Log de versión al iniciar
  useEffect(() => {
    const timestamp = new Date().toISOString();
    console.log(`🎭 LivenessFaceCapture v2.9 - ${timestamp}`);
    console.log('📋 Umbrales: left=<-3, right=>3, up=<-1.5, down=>1.5');
    console.log('🔧 Pitch: Negativo para arriba (<-1.5), positivo para abajo (>1.5)');
    console.log('✨ Mejoras: Umbrales más permisivos para mejor detección');
  }, []);

  useEffect(() => {
    initializeFaceMesh();
    return () => cleanup();
  }, []);

  useEffect(() => {
    if (step >= 1 && step <= captureSteps.length) {
      // CRÍTICO: Sincronizar ref con state
      stepRef.current = step;

      const targetStep = captureSteps[step - 1];
      setCurrentInstruction(targetStep.instruction);
      
      // Reproducir instrucción de voz
      const speechText = instructionToSpeech(targetStep.instruction);
      speakInstruction(speechText);
      
      setPoseProgress(0);
      progressCountRef.current = 0;
      isCapturingRef.current = false; // Resetear flag de captura

      // CRÍTICO: Limpiar cualquier timer del paso anterior
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }

      console.log(`🔄 Step ${step}/${captureSteps.length}: "${targetStep.id}" - Todo reseteado`);
      console.log(`   Instructional: ${targetStep.instruction}`);
      console.log(`   stepRef.current sincronizado a ${stepRef.current}`);
    }
  }, [step]);

  /**
   * Inicializar MediaPipe Face Mesh
   */
  const initializeFaceMesh = async () => {
    try {
      console.log('🎭 Inicializando MediaPipe Face Mesh...');

      const faceMesh = new FaceMesh({
        locateFile: (file) => {
          return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
        }
      });

      faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      faceMesh.onResults(onFaceMeshResults);
      faceMeshRef.current = faceMesh;

      // Inicializar cámara
      if (videoRef.current) {
        const camera = new Camera(videoRef.current, {
          onFrame: async () => {
            if (faceMeshRef.current && videoRef.current) {
              await faceMeshRef.current.send({ image: videoRef.current });
            }
          },
          width: 640,
          height: 480
        });

        cameraRef.current = camera;
        await camera.start();
        console.log('✅ Cámara iniciada');
      }
    } catch (error) {
      console.error('❌ Error inicializando Face Mesh:', error);
      if (onError) onError(error);
    }
  };

  /**
   * Procesar resultados de Face Mesh
   */
  const onFaceMeshResults = (results) => {
    if (!results.multiFaceLandmarks || results.multiFaceLandmarks.length === 0) {
      setFaceDetected(false);
      setPoseProgress(0);
      progressCountRef.current = 0;
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      // Reproducir sonido si el rostro se perdió
      if (lastFaceDetectedRef.current) {
        speakInstruction('Rostro no detectado');
      }
      lastFaceDetectedRef.current = false;
      return;
    }

    // Reproducir sonido si se detectó rostro por primera vez
    if (!lastFaceDetectedRef.current) {
      speakInstruction('Rostro detectado');
    }
    lastFaceDetectedRef.current = true;
    setFaceDetected(true);
    const landmarks = results.multiFaceLandmarks[0];

    // Calcular orientación de cabeza
    const pose = calculateHeadPose(landmarks);
    setHeadPose(pose);

    // Dibujar en canvas
    drawLandmarks(results, pose);

    // Verificar si cumple con la pose requerida
    // CRÍTICO: Usar stepRef.current en lugar de step para evitar closure stale
    const currentStepIndex = stepRef.current;
    if (currentStepIndex >= 1 && currentStepIndex <= captureSteps.length && !isCapturingRef.current) {
      const currentStep = captureSteps[currentStepIndex - 1];
      const stepId = currentStep.id;
      const poseMatch = currentStep.checkPose(pose);

      // Logging en cada step change o cuando hay match (para debugging)
      if (poseMatch || Math.random() < 0.1) { // Log cuando match o 10% del tiempo
        console.log(`[Step ${currentStepIndex}:"${stepId}"] yaw=${pose.yaw.toFixed(1)}°, pitch=${pose.pitch.toFixed(1)}°, match=${poseMatch}`);
      }

      if (poseMatch) {
        // Pose correcta - incrementar progreso
        if (!progressTimerRef.current) {
          console.log(`✓ Iniciando timer para "${stepId}"`);

          // Capturar el stepId en el closure para evitar problemas
          const captureStepId = stepId;

          progressTimerRef.current = setInterval(() => {
            progressCountRef.current += 5; // Incremento cada 30ms
            const newProgress = Math.min(progressCountRef.current, 100);
            setPoseProgress(newProgress);

            // Capturar cuando llegue a 100%
            if (newProgress >= 100 && !isCapturingRef.current) {
              clearInterval(progressTimerRef.current);
              progressTimerRef.current = null;
              isCapturingRef.current = true; // Marcar inmediatamente como capturando
              console.log(`📸 Capturando en timer para "${captureStepId}"`);
              captureFrame(captureStepId, results.image);
            }
          }, 30); // Actualizar cada 30ms = ~600ms para llegar a 100
        }
      } else {
        // Pose incorrecta - decrementar progreso lentamente (más perdonador)
        if (progressTimerRef.current) {
          clearInterval(progressTimerRef.current);
          progressTimerRef.current = null;
        }
        progressCountRef.current = Math.max(0, progressCountRef.current - 3); // Decrementar lento
        setPoseProgress(Math.max(0, progressCountRef.current));
      }
    }
  };

  /**
   * Calcular orientación de cabeza usando landmarks
   */
  const calculateHeadPose = (landmarks) => {
    const nose = landmarks[1];
    const noseTip = landmarks[4];
    const chin = landmarks[152];
    const forehead = landmarks[10];
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const leftEyeOuter = landmarks[130];
    const rightEyeOuter = landmarks[359];
    const leftMouth = landmarks[61];
    const rightMouth = landmarks[291];

    // Método mejorado: Calcular yaw usando distancia entre ojos y nariz
    // Cuando giras la cabeza, la nariz se mueve hacia un lado
    const eyeCenter = {
      x: (leftEye.x + rightEye.x) / 2,
      y: (leftEye.y + rightEye.y) / 2
    };

    // Calcular distancia de cada ojo a la nariz (método más sensible)
    const distLeftEyeToNose = Math.abs(leftEye.x - nose.x);
    const distRightEyeToNose = Math.abs(rightEye.x - nose.x);

    // El ratio entre las distancias indica hacia dónde giras
    // Si giras a la izquierda, ojo derecho estará más lejos de la nariz
    const eyeDistanceRatio = (distRightEyeToNose - distLeftEyeToNose) * 200;

    // También calcular usando posición de nariz relativa al centro
    const noseOffset = (nose.x - eyeCenter.x) * 200;

    // Combinar ambos métodos para mayor precisión
    const yaw = (eyeDistanceRatio + noseOffset) / 2;

    // Calcular pitch (rotación vertical: arriba/abajo)
    const faceCenter = {
      x: (forehead.x + chin.x) / 2,
      y: (forehead.y + chin.y) / 2
    };
    // Multiplicar por 150 para hacer los valores más pronunciados
    const pitch = (nose.y - faceCenter.y) * 150;

    // Calcular roll (inclinación lateral)
    const roll = Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x) * (180 / Math.PI);

    return { yaw, pitch, roll };
  };

  /**
   * Dibujar landmarks faciales
   */
  const drawLandmarks = (results, pose) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;

    // Limpiar canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Dibujar imagen de video (espejo)
    ctx.save();
    ctx.scale(-1, 1);
    ctx.translate(-canvas.width, 0);
    ctx.drawImage(results.image, 0, 0, canvas.width, canvas.height);
    ctx.restore();

    // Dibujar landmarks faciales
    if (results.multiFaceLandmarks) {
      for (const landmarks of results.multiFaceLandmarks) {
        ctx.fillStyle = 'rgba(16, 185, 129, 0.6)';
        for (const landmark of landmarks) {
          const x = canvas.width - (landmark.x * canvas.width);
          const y = landmark.y * canvas.height;
          ctx.beginPath();
          ctx.arc(x, y, 1, 0, 2 * Math.PI);
          ctx.fill();
        }
      }
    }

    // Mostrar valores de pose
    ctx.fillStyle = 'white';
    ctx.font = '12px monospace';
    ctx.fillText(`Yaw: ${pose.yaw.toFixed(1)}°`, 10, 20);
    ctx.fillText(`Pitch: ${pose.pitch.toFixed(1)}°`, 10, 40);
    ctx.fillText(`Progress: ${poseProgress.toFixed(0)}%`, 10, 60);
  };

  /**
   * Capturar frame actual
   */
  const captureFrame = (angleId, image) => {
    // Verificar si ya se capturó este ángulo
    if (capturedAnglesRef.current.has(angleId)) {
      console.log(`⚠️ Frame ${angleId} ya fue capturado, ignorando duplicado`);
      return;
    }

    // Marcar ángulo como capturado inmediatamente
    capturedAnglesRef.current.add(angleId);
    console.log(`📸 Capturando frame: ${angleId} (${capturedAnglesRef.current.size}/${captureSteps.length})`);

    // Convertir imagen a base64
    const canvas = document.createElement('canvas');
    canvas.width = 320;
    canvas.height = 320;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    const base64Image = canvas.toDataURL('image/jpeg', 0.85);

    // Crear objeto pose serializable (solo números, sin referencias)
    const cleanPose = {
      yaw: Number(headPose.yaw) || 0,
      pitch: Number(headPose.pitch) || 0,
      roll: Number(headPose.roll) || 0
    };

    const newFrame = {
      angle: angleId,
      image: base64Image,
      timestamp: Date.now(),
      pose: cleanPose
    };

    setCapturedFrames(prev => {
      const updatedFrames = [...prev, newFrame];

      // Si completamos todas las capturas, enviar
      if (updatedFrames.length === captureSteps.length) {
        addTimeout(() => handleComplete(updatedFrames), 500);
      }

      return updatedFrames;
    });

    // Avanzar al siguiente paso si no es el último
    if (stepRef.current < captureSteps.length) {
      addTimeout(() => {
        const nextStep = stepRef.current + 1;
        setStep(nextStep);
      }, 500);
    }
  };

  /**
   * Completar captura
   */
  const handleComplete = (frames) => {
    console.log(`✅ Captura completada: ${frames.length} frames`);

    // Anunciar que la identificación terminó
    speakInstruction('Identificación terminada');

    // Detener timers y cámara INMEDIATAMENTE, pero NO cerrar faceMesh todavía
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    if (cameraRef.current) {
      cameraRef.current.stop();
    }

    // Sanitizar frames antes de enviar (asegurar que sean JSON-serializables)
    const sanitizedFrames = frames.map(frame => ({
      angle: String(frame.angle),
      image: String(frame.image),
      timestamp: Number(frame.timestamp),
      pose: {
        yaw: Number(frame.pose?.yaw || 0),
        pitch: Number(frame.pose?.pitch || 0),
        roll: Number(frame.pose?.roll || 0)
      }
    }));

    console.log('📦 Frames sanitizados listos para enviar');

    if (onComplete) {
      onComplete(sanitizedFrames);
    }

    // CRÍTICO: Cerrar faceMesh DESPUÉS con delay para evitar BindingError
    // MediaPipe puede tener procesamiento pendiente en segundo plano
    setTimeout(() => {
      if (faceMeshRef.current) {
        console.log('🧹 Cerrando MediaPipe FaceMesh...');
        faceMeshRef.current.close();
        faceMeshRef.current = null;
      }
    }, 1000); // 1 segundo de delay
  };

  /**
   * Limpiar recursos
   */
  const cleanup = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    if (cameraRef.current) {
      cameraRef.current.stop();
    }
    if (faceMeshRef.current) {
      faceMeshRef.current.close();
    }
    // Resetear refs
    isCapturingRef.current = false;
    capturedAnglesRef.current.clear();
    progressCountRef.current = 0;
  };

  /**
   * Cancelar captura
   */
  const handleCancel = () => {
    cleanup();
    if (onError) onError('Captura cancelada');
  };

  const currentStepColor = step >= 1 && step <= captureSteps.length
    ? captureSteps[step - 1].color
    : '#3B82F6';

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Instrucciones */}
      <div className="mb-4 text-center">
        <div
          className="inline-block px-6 py-3 rounded-full text-white font-bold text-xl shadow-lg animate-pulse"
          style={{ backgroundColor: currentStepColor }}
        >
          {currentInstruction}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-semibold text-gray-600">
            Paso {step} de {captureSteps.length}
          </span>
          <span className="text-sm text-gray-500">
            {capturedFrames.length} capturadas
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="h-3 rounded-full transition-all duration-300"
            style={{
              width: `${(step / captureSteps.length) * 100}%`,
              backgroundColor: currentStepColor
            }}
          />
        </div>
      </div>

      {/* Video Canvas */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
        <video
          ref={videoRef}
          className="w-full h-auto opacity-0 absolute"
          autoPlay
          playsInline
          muted
        />
        <canvas
          ref={canvasRef}
          className="w-full h-auto"
          style={{ transform: 'scaleX(1)' }}
        />

        {/* Estado de detección - Movido a inferior izquierda */}
        <div className="absolute bottom-4 left-4 flex flex-col gap-2">
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
            faceDetected ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {faceDetected ? '✓ Rostro detectado' : '✗ Sin rostro'}
          </div>
        </div>

        {/* Progreso en superior derecha */}
        {faceDetected && (
          <div className="absolute top-4 right-4">
            <div className="px-3 py-1 rounded-full bg-blue-500 text-white text-sm font-semibold">
              {poseProgress.toFixed(0)}%
            </div>
          </div>
        )}

        {/* Indicadores de pasos completados - Centro inferior */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
          {captureSteps.map((s, idx) => (
            <div
              key={s.id}
              className={`w-3 h-3 rounded-full transition-all ${
                capturedFrames.some(f => f.angle === s.id)
                  ? 'scale-125'
                  : idx === step - 1
                  ? 'scale-110 animate-pulse'
                  : 'scale-100'
              }`}
              style={{
                backgroundColor: capturedFrames.some(f => f.angle === s.id)
                  ? s.color
                  : idx === step - 1
                  ? s.color
                  : 'rgba(255,255,255,0.3)'
              }}
            />
          ))}
        </div>
      </div>

      {/* Botón cancelar */}
      <div className="mt-4 text-center">
        <button
          onClick={handleCancel}
          className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 font-semibold transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default LivenessFaceCapture;
